/* eslint-disable import/no-extraneous-dependencies */
import { Buffer } from 'buffer';
import { jwtDecode } from 'jwt-decode';
import { safeJsonParse } from '@rcw/utils';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
});

export const isSafari = navigator.vendor && navigator.vendor.indexOf('Apple') > -1
&& navigator.userAgent
&& navigator.userAgent.indexOf('CriOS') === -1
&& navigator.userAgent.indexOf('FxiOS') === -1;

export const formatCurrency = currencyFormatter.format;
export const isNumber = (value) => typeof value === 'number';
export const isString = (value) => typeof value === 'string';
export const isFunction = (value) => typeof value === 'function';
export const isObject = (value) => typeof value === 'object' && value !== null;
export const pickBy = (object) => {
  const obj = {};
  for (const key in object) {
    if (object[key]) {
      obj[key] = object[key];
    }
  }
  return obj;
};

export const goToCustomUrl = (url) => {
  window.location.href = url;
};

export const getUrlOffersParams = (location) => {
  let offerSKU = null;
  let offerId = null;
  let loc = null;

  const decodedParams = Buffer.from(location.search, 'base64').toString() || '';
  const hasCorrectParams = decodedParams.includes('offer') && decodedParams.includes('idx');
  const hasLocParams = decodedParams.includes('loc');
  const splitParams = decodedParams?.split('&');

  if (hasCorrectParams) {
    // check if has valid offer params
    const offer = splitParams.filter((el) => el.includes('offer='))[0];
    const idx = splitParams.filter((el) => el.includes('idx='))[0];
    // parse them appropriately
    offerSKU = typeof offer === 'string'
      ? offer.replace('offer=', '')
      : null;
    offerId = typeof idx === 'string'
      ? parseInt(idx.replace('idx=', ''), 10)
      : null;
  }

  // check if localizations are present
  if (hasLocParams) {
    const locParam = splitParams.filter((el) => el.includes('loc='))[0];
    loc = typeof locParam === 'string'
      ? locParam.replace('loc=', '')
      : null;
  }

  return { offerSKU, offerId, loc };
};

export const getLocalizationParam = (search) => {
  const urlParams = new URLSearchParams(search);
  const loc = urlParams.get('loc');

  return loc;
};

export const getLocalizationCopyContent = (loc, content) => {
  // check if there's content for that localization
  if (content && content[loc]) {
    return content[loc];
  }
  // else return the default content
  return content.en;
};

export const getScreenSize = (width) => {
  if (width < 320) {
    return 'xs';
  }
  if (width >= 320 && width < 720) {
    return 'sm';
  }
  if (width >= 720 && width < 1024) {
    return 'md';
  }
  if (width >= 1024 && width < 1440) {
    return 'lg';
  }
  if (width >= 1440) {
    return 'xl';
  }
};

export const goToLogin = (sku, offerIdx, loginAndRedirectUrl, locParam) => {
  const encodedParams = Buffer.from(`offer=${sku}&idx=${offerIdx}${locParam ? `&${locParam}` : ''}`).toString('base64');
  // fer: don't forget to add as slash before the encoded params
  // otherwise the params will be lost
  const redirectTo = `${loginAndRedirectUrl}?${encodedParams}`;
  window.location.href = redirectTo;
};

/**
 * Determines if the current time falls between the given start and end dates.
 *
 * @param {boolean} enableCountdownBanner - Whether the countdown banner is enabled.
 * @param {string} startDate - The UTC start date in ISO 8601 format.
 * @param {string} endDate - The UTC end date in ISO 8601 format.
 * @returns {boolean} - True if the current time is within the range and the banner is enabled.
 */
export function shouldShowPromoSale(enableCountdownBanner, startDate, endDate) {
  if (!enableCountdownBanner) return false;

  const currentTime = new Date().getTime();
  const startTimeUTC = Date.parse(startDate);
  const endTimeUTC = Date.parse(endDate);

  return currentTime >= startTimeUTC && currentTime <= endTimeUTC;
}

export const isInTheCart = (productId) => {
  const cart = localStorage.getItem('cart');
  if (cart) {
    const parsedCart = safeJsonParse(cart, []);
    return parsedCart.some((p) => p.id === productId);
  }
  return false;
};

export const checkIfTokenExpired = (token) => {
  if (!token) return true;

  try {
    const decoded = jwtDecode(token);

    if (!decoded.exp) return true;

    const currentTime = Math.floor(Date.now() / 1000);

    return decoded.exp < currentTime;
  } catch (error) {
    console.error('Invalid token:', error);
    return true;
  }
};

export const capitalize = (str = '') => {
  if (typeof str !== 'string' || !str.length) {
    return '';
  }

  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const slugify = (value) => {
  return value
    .toLowerCase()
    .trim()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-');
};

export const checkIfProductIsNew = (ReleaseDate) => {
  if (!ReleaseDate) return false;

  const releaseDateMatch = ReleaseDate.match(/^(\d{4}-\d{2}-\d{2})(\+(\d+))?$/);
  if (!releaseDateMatch) return false;

  const releaseDate = new Date(releaseDateMatch[1]);
  const currentDate = new Date();

  // Clear time parts to compare by date only
  releaseDate.setHours(0, 0, 0, 0);
  currentDate.setHours(0, 0, 0, 0);

  if (releaseDateMatch[3]) {
    const daysVisible = parseInt(releaseDateMatch[3], 10);
    const expirationDate = new Date(releaseDate);
    expirationDate.setDate(expirationDate.getDate() + daysVisible);

    return currentDate >= releaseDate && currentDate <= expirationDate;
  }

  return currentDate.getTime() === releaseDate.getTime();
};
